import { useCallback, useEffect, useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { SHOP_ROLES, formatRole } from '../../utils/format';

const emptyMember = {
  email: '',
  first_name: '',
  last_name: '',
  role: 'manager',
};

export default function MembersPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!selectedShopId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listMembers(token, selectedShopId);
      setMembers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleAdd = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.addMember(token, selectedShopId, form);
      setModalOpen(false);
      setForm(emptyMember);
      await loadMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this team member?')) {
      return;
    }
    setError(null);
    try {
      const token = await getToken();
      await shopApi.removeMember(token, selectedShopId, userId);
      await loadMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Team"
        description="Invite and manage team members who can access this store."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Add team member
          </button>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : members.length === 0 ? (
        <EmptyState message="No team members yet." />
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <td>
                    {member.user
                      ? `${member.user.first_name} ${member.user.last_name}`
                      : `User #${member.user_id}`}
                  </td>
                  <td>{member.user?.email}</td>
                  <td>
                    <select
                      className="role-select"
                      value={member.role}
                      onChange={async (event) => {
                        setError(null);
                        try {
                          const token = await getToken();
                          await shopApi.updateMember(token, selectedShopId, member.user_id, {
                            role: event.target.value,
                          });
                          await loadMembers();
                        } catch (err) {
                          setError(err.message);
                        }
                      }}
                    >
                      {SHOP_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {formatRole(role)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemove(member.user_id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        title="Add staff member"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="add-member-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Adding…' : 'Add member'}
            </button>
          </>
        }
      >
        <form id="add-member-form" className="form-grid" onSubmit={handleAdd}>
          <div className="form-field">
            <label htmlFor="member-email">Email</label>
            <input
              id="member-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              required
            />
          </div>
          <div className="form-grid two-col">
            <div className="form-field">
              <label htmlFor="member-first">First name</label>
              <input
                id="member-first"
                value={form.first_name}
                onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="member-last">Last name</label>
              <input
                id="member-last"
                value={form.last_name}
                onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="member-role">Role</label>
            <select
              id="member-role"
              value={form.role}
              onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}
            >
              {SHOP_ROLES.map((role) => (
                <option key={role} value={role}>
                  {formatRole(role)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>
    </RequireShop>
  );
}
